import React, { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { IconLink, IconPlayerPlay } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { useCampaignWebhooks } from '@features/Admin/Campaign/hooks/useCampaignWebhooks';
import {
  getCampaignWebhookErrorMessage,
  showCampaignWebhookSuccessToast,
} from '@features/Admin/Campaign/utils/campaign-webhook-ui';
import {
  CampaignWebhookAuthType,
  CampaignWebhookDraft,
  CampaignWebhookValidationErrors,
  validateCampaignWebhookDraft,
} from '@features/Admin/Campaign/utils/campaign-webhook-mapper';

import CampaignWebhookCard from './CampaignWebhookCard';
import CampaignWebhookDeleteDialog from './CampaignWebhookDeleteDialog';
import CampaignWebhookDispatchDialog from './CampaignWebhookDispatchDialog';
import CampaignWebhookPopup from './CampaignWebhookPopup';

interface CampaignWebhookSectionProps {
  campaignId: string;
  isDisabled: boolean;
}

const CampaignWebhookSection: React.FC<CampaignWebhookSectionProps> = ({
  campaignId,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    webhooks,
    endedCalls,
    isLoading,
    isEndedCallsLoading,
    isSavingWebhook,
    isDeletingWebhook,
    isDispatchingWebhook,
    createDraft,
    saveWebhook,
    deleteWebhook,
    dispatchWebhooks,
  } = useCampaignWebhooks(campaignId);

  const [draft, setDraft] = useState<CampaignWebhookDraft | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [errors, setErrors] = useState<CampaignWebhookValidationErrors>({});
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState<CampaignWebhookDraft | null>(null);

  const hasEnabledWebhooks = useMemo(
    () => webhooks.some((webhook) => webhook.isEnabled),
    [webhooks],
  );

  const isBusy = isSavingWebhook || isDeletingWebhook || isDispatchingWebhook;
  const isDispatchDisabled =
    isDisabled || !hasEnabledWebhooks || endedCalls.length === 0 || isDispatchingWebhook;

  useEffect(() => {
    if (dispatchDialogOpen && endedCalls.length > 0 && !selectedCallId) {
      setSelectedCallId(endedCalls[0].id);
    }
  }, [dispatchDialogOpen, endedCalls, selectedCallId]);

  const handleCreate = () => {
    setDraft(createDraft());
    setIsPopupOpen(true);
    setErrors({});
  };

  const handleEdit = (webhook: CampaignWebhookDraft) => {
    setDraft({
      ...webhook,
      headers: webhook.headers.map((header) => ({ ...header })),
      authConfig: { ...webhook.authConfig },
    });
    setIsPopupOpen(true);
    setErrors({});
  };

  const handleCancel = () => {
    setDraft(null);
    setIsPopupOpen(false);
    setErrors({});
  };

  const handleDraftChange = <K extends keyof CampaignWebhookDraft>(
    key: K,
    value: CampaignWebhookDraft[K],
  ) => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        [key]: value,
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, [key]: undefined }));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        headers: prevDraft.headers.map((header, headerIndex) =>
          headerIndex === index ? { ...header, [field]: value } : header,
        ),
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, headers: undefined }));
  };

  const handleAddHeader = () => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        headers: [...prevDraft.headers, { key: '', value: '' }],
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, headers: undefined }));
  };

  const handleRemoveHeader = (index: number) => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        headers: prevDraft.headers.filter((_, headerIndex) => headerIndex !== index),
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, headers: undefined }));
  };

  const handleAuthFieldChange = (
    field: keyof CampaignWebhookDraft['authConfig'],
    value: string,
  ) => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        authConfig: {
          ...prevDraft.authConfig,
          [field]: value,
        },
        isAuthDirty: true,
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, auth: undefined }));
  };

  const handleAuthTypeChange = (authType: CampaignWebhookAuthType) => {
    setDraft((prevDraft) => {
      if (!prevDraft) {
        return prevDraft;
      }

      return {
        ...prevDraft,
        authType,
        authConfig: {
          username: '',
          password: '',
          token: '',
        },
        isAuthDirty: prevDraft.originalAuthType !== authType || prevDraft.hasStoredAuth,
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, auth: undefined }));
  };

  const handleSave = async () => {
    if (!draft) {
      return;
    }

    const validationResult = validateCampaignWebhookDraft(draft);

    if (!validationResult.isValid) {
      setErrors(validationResult.errors);
      return;
    }

    try {
      await saveWebhook(draft);
      showCampaignWebhookSuccessToast(
        draft.id
          ? t('campaignPage.webhooks.messages.updated')
          : t('campaignPage.webhooks.messages.created'),
      );
      handleCancel();
    } catch (error) {
      toast.error(
        getCampaignWebhookErrorMessage(error, 'campaignPage.webhooks.messages.saveError'),
      );
    }
  };

  const handleToggleEnabled = async (webhook: CampaignWebhookDraft, checked: boolean) => {
    try {
      await saveWebhook({
        ...webhook,
        isEnabled: checked,
      });
      showCampaignWebhookSuccessToast(
        checked
          ? t('campaignPage.webhooks.messages.enabled')
          : t('campaignPage.webhooks.messages.disabled'),
      );
    } catch (error) {
      toast.error(
        getCampaignWebhookErrorMessage(error, 'campaignPage.webhooks.messages.saveError'),
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate?.id) {
      return;
    }

    try {
      await deleteWebhook(deleteCandidate.id);
      showCampaignWebhookSuccessToast(t('campaignPage.webhooks.messages.deleted'));
      if (draft?.id === deleteCandidate.id) {
        handleCancel();
      }
      setDeleteCandidate(null);
    } catch (error) {
      toast.error(
        getCampaignWebhookErrorMessage(error, 'campaignPage.webhooks.messages.deleteError'),
      );
    }
  };

  const handleDispatch = async () => {
    if (!selectedCallId) {
      return;
    }

    try {
      await dispatchWebhooks(selectedCallId);
      showCampaignWebhookSuccessToast(t('campaignPage.webhooks.messages.dispatched'));
      setDispatchDialogOpen(false);
    } catch (error) {
      toast.error(
        getCampaignWebhookErrorMessage(error, 'campaignPage.webhooks.messages.dispatchError'),
      );
    }
  };

  return (
    <>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              svg: {
                color: theme.palette.getContrastText(theme.palette.background.default),
              },
            }}>
            <IconLink size={22} />
            <Typography fontSize="18px" fontWeight={600}>
              {t('campaignPage.webhooks.title')}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconPlayerPlay size={16} />}
              onClick={() => setDispatchDialogOpen(true)}
              disabled={isDispatchDisabled}>
              {t('campaignPage.webhooks.testDispatch')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={isDisabled || isBusy}>
              {t('campaignPage.webhooks.addWebhook')}
            </Button>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {t('campaignPage.webhooks.subtitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('campaignPage.webhooks.dispatchHint')}
        </Typography>

        {!hasEnabledWebhooks ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('campaignPage.webhooks.testDispatchDisabledNoEnabled')}
          </Typography>
        ) : null}

        {!isEndedCallsLoading && endedCalls.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('campaignPage.webhooks.testDispatchDisabledNoCalls')}
          </Typography>
        ) : null}

        {isLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : webhooks.length > 0 ? (
          <Box>
            {webhooks.map((webhook) => (
              <CampaignWebhookCard
                key={webhook.id}
                webhook={webhook}
                disabled={isDisabled}
                busy={isBusy}
                onEdit={handleEdit}
                onDelete={setDeleteCandidate}
                onToggle={handleToggleEnabled}
              />
            ))}
          </Box>
        ) : (
          <BorderedGrid
            sx={{
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: theme.palette.grey[50],
            }}>
            <Typography fontSize="15px" fontWeight={600} sx={{ mb: 1 }}>
              {t('campaignPage.webhooks.emptyTitle')}
            </Typography>
            <Typography color="text.secondary">
              {t('campaignPage.webhooks.emptyDescription')}
            </Typography>
          </BorderedGrid>
        )}
      </Box>

      <CampaignWebhookPopup
        open={isPopupOpen}
        draft={draft}
        errors={errors}
        disabled={isDisabled || isBusy}
        isLoading={isSavingWebhook}
        onChange={handleDraftChange}
        onAuthTypeChange={handleAuthTypeChange}
        onAuthFieldChange={handleAuthFieldChange}
        onHeaderChange={handleHeaderChange}
        onAddHeader={handleAddHeader}
        onRemoveHeader={handleRemoveHeader}
        onClose={handleCancel}
        onSave={handleSave}
      />

      <CampaignWebhookDispatchDialog
        open={dispatchDialogOpen}
        endedCalls={endedCalls}
        selectedCallId={selectedCallId}
        disabled={isDispatchDisabled}
        isLoading={isDispatchingWebhook}
        isCallsLoading={isEndedCallsLoading}
        onChangeCall={setSelectedCallId}
        onClose={() => setDispatchDialogOpen(false)}
        onConfirm={handleDispatch}
      />

      <CampaignWebhookDeleteDialog
        open={Boolean(deleteCandidate)}
        name={deleteCandidate?.name}
        isLoading={isDeletingWebhook}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default CampaignWebhookSection;
